import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { type MouseEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { defaultRoute } from '@/shared/config';
import { useRouteHandle } from '@/shared/lib';
import { radius } from '@/shared/theme';
import { Icon } from '@/shared/ui';
import { type OpenTab, useTabsStore } from '../model/tabs-store';

/** Bar of open screens under the header; opens a tab for every visited page. */
export function ScreenTabs() {
  const { t } = useTranslation();
  const handle = useRouteHandle();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const tabs = useTabsStore((s) => s.tabs);
  const open = useTabsStore((s) => s.open);
  const close = useTabsStore((s) => s.close);
  const closeOthers = useTabsStore((s) => s.closeOthers);
  const closeToRight = useTabsStore((s) => s.closeToRight);
  const reset = useTabsStore((s) => s.reset);
  const [menu, setMenu] = useState<{ tab: OpenTab; top: number; left: number } | null>(null);

  useEffect(() => {
    if (handle) open({ ...handle, key: pathname, path: pathname + search });
  }, [handle, pathname, search, open]);

  const onClose = (tab: OpenTab) => {
    const next = close(tab.key);
    if (tab.key === pathname) navigate(next?.path ?? defaultRoute);
  };

  // Right-click (or the context-menu key) on a tab opens VS Code-style close actions. The
  // keyboard variant reports no pointer position, so the menu then anchors to the tab itself.
  const openMenu = (event: MouseEvent<HTMLElement>, tab: OpenTab) => {
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    const fromKeyboard = event.clientX === 0 && event.clientY === 0;
    setMenu({
      tab,
      top: fromKeyboard ? rect.bottom : event.clientY,
      left: fromKeyboard ? rect.left : event.clientX,
    });
  };

  const run = (action: (tab: OpenTab) => void) => {
    if (!menu) return;
    const { tab } = menu;
    setMenu(null);
    action(tab);
  };

  const keepOnly = (tab: OpenTab) => {
    closeOthers(tab.key);
    if (tab.key !== pathname) navigate(tab.path);
  };

  const keepLeft = (tab: OpenTab) => {
    const idx = tabs.findIndex((x) => x.key === tab.key);
    const activeIdx = tabs.findIndex((x) => x.key === pathname);
    closeToRight(tab.key);
    // The screen on show was to the right, so it is gone: show the tab that was clicked.
    if (activeIdx > idx) navigate(tab.path);
  };

  const closeAll = () => {
    reset();
    // The bar always shows the current screen; on the start page it has to be reopened by hand
    // because the route (and so the effect above) does not change.
    if (pathname === defaultRoute && handle) {
      open({ ...handle, key: pathname, path: pathname + search });
    } else {
      navigate(defaultRoute);
    }
  };

  if (tabs.length === 0) return null;
  const menuIndex = menu ? tabs.findIndex((x) => x.key === menu.tab.key) : -1;

  return (
    <Box
      role="tablist"
      aria-label={t('nav.openScreens')}
      sx={(theme) => ({
        flex: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        p: '10px 20px',
        overflowX: 'auto',
        background: theme.vars.palette.md.panel,
        borderBottom: `1px solid ${theme.vars.palette.md.border}`,
      })}
    >
      {tabs.map((tab) => {
        const active = tab.key === pathname;
        const label = t(`nav.items.${tab.page}`);
        return (
          <Box
            key={tab.key}
            onContextMenu={(event) => openMenu(event, tab)}
            sx={(theme) => ({
              flex: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              borderRadius: radius.sm,
              color: active ? theme.vars.palette.md.primary : theme.vars.palette.md.muted,
              background: active ? theme.vars.palette.md.primary050 : 'transparent',
              '&:hover': active
                ? {}
                : {
                    color: theme.vars.palette.md.text,
                    background: `color-mix(in srgb, ${theme.vars.palette.md.text} 5%, transparent)`,
                  },
            })}
          >
            <ButtonBase
              role="tab"
              aria-selected={active}
              onClick={() => navigate(tab.path)}
              sx={{
                gap: 1,
                p: '7px 4px 7px 12px',
                borderRadius: radius.sm,
                fontSize: 13,
                fontWeight: active ? 700 : 600,
                whiteSpace: 'nowrap',
              }}
            >
              <Icon name={tab.icon} size={15} />
              {label}
            </ButtonBase>
            <ButtonBase
              aria-label={`${t('nav.closeTab')}: ${label}`}
              onClick={() => onClose(tab)}
              sx={(theme) => ({
                p: '2px',
                mr: '8px',
                borderRadius: radius.xs,
                '&:hover': {
                  background: `color-mix(in srgb, ${theme.vars.palette.md.text} 8%, transparent)`,
                },
              })}
            >
              <Icon name="x" size={14} />
            </ButtonBase>
          </Box>
        );
      })}
      <Menu
        open={!!menu}
        onClose={() => setMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={menu ? { top: menu.top, left: menu.left } : undefined}
        slotProps={{ list: { 'aria-label': t('nav.tabMenu.label'), dense: true } }}
      >
        <MenuItem onClick={() => run(onClose)}>{t('nav.tabMenu.close')}</MenuItem>
        <MenuItem onClick={() => run(keepOnly)} disabled={tabs.length < 2}>
          {t('nav.tabMenu.closeOthers')}
        </MenuItem>
        <MenuItem onClick={() => run(keepLeft)} disabled={menuIndex === tabs.length - 1}>
          {t('nav.tabMenu.closeRight')}
        </MenuItem>
        <MenuItem onClick={() => run(closeAll)}>{t('nav.tabMenu.closeAll')}</MenuItem>
      </Menu>
    </Box>
  );
}
